
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import ModalFullScreenNew from "@/components/modal/ModalFullScreenNew.vue";
import FormToggleNew from "@/components/form/FormToggleNew.vue";
import FormInputGroupNew from "@/components/form/FormInputGroupNew.vue";
import FormSelectNew from "@/components/form/FormSelectNew.vue";
import UIButton from "@/components/button/ui-button.vue";
import TransportationMultiselect from "@/components/autocompleteMultiselects/TransportationMultiselect.vue";
import StarRating from "./StarRating.vue";
// Using native HTML5 drag and drop instead of external package
import { User } from "@/generated/graphql";
import DropdownOption from "@/entities/DropdownOption";
import SAVE_SMART_MATCH_CONFIG from "@/apollo/smartMatch/SaveSmartMatchConfig.gql";
import SAVE_SMART_MATCH_CONFIG_ADMIN from "@/apollo/smartMatch/SaveSmartMatchConfigAdmin.gql";
import SAVE_TRANSPORTATION_PREFERENCES from "@/apollo/smartMatch/SaveTransportationPreferences.gql";
import SAVE_CRITERIA_ORDER from "@/apollo/smartMatch/SaveCriteriaOrder.gql";
import GET_SMART_MATCH_CONFIG from "@/apollo/smartMatch/GetSmartMatchConfig.gql";
import GET_SMART_MATCH_CONFIG_BY_MANAGER from "@/apollo/smartMatch/GetSmartMatchConfigByManager.gql";
import GET_SMART_MATCH_CONFIG_BY_ADMIN from "@/apollo/smartMatch/GetSmartMatchConfigByAdmin.gql";
import GET_MANAGER_BY_USER_ID from "@/apollo/smartMatch/GetManagerByUserId.gql";
import GET_ADMIN_BY_USER_ID from "@/apollo/smartMatch/GetAdminByUserId.gql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";

interface SmartMatchConfig {
  smart_match_enabled: boolean;
  availability_enabled: boolean;
  exclude_do_not_use_enabled: boolean;
  required_skills_enabled: boolean;
  location_radius_enabled: boolean;
  location_radius_value: number;
  location_radius_unit: string;
  preferred_language_enabled: boolean;
  preferred_language_proficiency: number;
  soft_skills_enabled: boolean;
  title_type_match_enabled: boolean;
  previous_engagements_enabled: boolean;
  previous_engagements_min: number;
  transportation_enabled: boolean;
  rating_enabled: boolean;
  rating_min_stars: number;
  favourited_resource_enabled: boolean;
  hours_available_enabled: boolean;
  hours_available_min: number;
}

interface NiceToHaveCriterion {
  type: string;
  label: string;
  description: string;
  enabled: boolean;
  sort_order: number;
}

@Component({
  components: {
    ModalFullScreenNew,
    FormToggleNew,
    FormInputGroupNew,
    FormSelectNew,
    UIButton,
    TransportationMultiselect,
    StarRating,
    ValidationObserver,
  },
})
export default class SmartMatchConfigModal extends Vue {
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  isLoading = false;
  managerId: string | null = null;
  adminId: string | null = null;
  isAdmin = false;

  formData: SmartMatchConfig = {
    smart_match_enabled: true,
    availability_enabled: true,
    exclude_do_not_use_enabled: true,
    required_skills_enabled: true,
    location_radius_enabled: true,
    location_radius_value: 25,
    location_radius_unit: 'kilometres',
    preferred_language_enabled: true,
    preferred_language_proficiency: 3,
    soft_skills_enabled: true,
    title_type_match_enabled: true,
    previous_engagements_enabled: true,
    previous_engagements_min: 0,
    transportation_enabled: true,
    rating_enabled: true,
    rating_min_stars: 3,
    favourited_resource_enabled: true,
    hours_available_enabled: true,
    hours_available_min: 1,
  };

  niceToHaveCriteria: NiceToHaveCriterion[] = [
    {
      type: 'soft_skills',
      label: 'Soft Skills',
      description: 'When toggled on, resources must have soft skills that match with project requirements.',
      enabled: true,
      sort_order: 1,
    },
    {
      type: 'title_type_match',
      label: 'Title/Type Match',
      description: "Resource's Title/Type must match client's Support Type.",
      enabled: true,
      sort_order: 2,
    },
    {
      type: 'previous_engagements',
      label: 'Previous Engagements',
      description: 'Set a preference for minimum number of previous visits with a client.',
      enabled: true,
      sort_order: 3,
    },
    {
      type: 'transportation',
      label: 'Transportation Preference',
      description: 'Preferred transportation mode for resource to client.',
      enabled: true,
      sort_order: 4,
    },
    {
      type: 'rating',
      label: 'Rating',
      description: 'Set a minimum rating preference.',
      enabled: true,
      sort_order: 5,
    },
    {
      type: 'favourited_resource',
      label: 'Favourited Resource',
      description: 'Preference for a Resource that has been favourited.',
      enabled: true,
      sort_order: 6,
    },
    {
      type: 'hours_available',
      label: 'Hours Available',
      description: 'Set a preference for how many hours the Resource should have available in their work week.',
      enabled: true,
      sort_order: 7,
    },
  ];

  selectedTransportation: DropdownOption[] = [];

  // Drag and drop state
  draggedIndex: number | null = null;
  dragOverIndex: number | null = null;
  isDraggingSlider: boolean = false;

  // Previous engagements slider mapping
  previousEngagementsSliderValue: number = 0;

  // Watchers to sync form data with criteria
  @Watch('previousEngagementsSliderValue')
  onPreviousEngagementsSliderChange(newValue: number) {
    this.formData.previous_engagements_min = this.sliderValueToEngagements(newValue);
  }

  @Watch('formData.previous_engagements_min')
  onPreviousEngagementsMinChange(newValue: number) {
    this.previousEngagementsSliderValue = this.engagementsToSliderValue(newValue);
  }

  @Watch('niceToHaveCriteria', { deep: true })
  onCriteriaChange() {
    // Sync criterion enabled states with form data
    this.niceToHaveCriteria.forEach(criterion => {
      switch (criterion.type) {
        case 'soft_skills':
          this.formData.soft_skills_enabled = criterion.enabled;
          break;
        case 'title_type_match':
          this.formData.title_type_match_enabled = criterion.enabled;
          break;
        case 'previous_engagements':
          this.formData.previous_engagements_enabled = criterion.enabled;
          break;
        case 'transportation':
          this.formData.transportation_enabled = criterion.enabled;
          break;
        case 'rating':
          this.formData.rating_enabled = criterion.enabled;
          break;
        case 'favourited_resource':
          this.formData.favourited_resource_enabled = criterion.enabled;
          break;
        case 'hours_available':
          this.formData.hours_available_enabled = criterion.enabled;
          break;
      }
    });
  }

  get radiusUnitOptions() {
    return [
      { value: 'kilometres', label: 'Kilometres' },
      { value: 'miles', label: 'Miles' },
    ];
  }

  get proficiencyOptions() {
    return [
      { value: 1, label: 'Native' },
      { value: 2, label: 'Fluent/Proficient' },
      { value: 3, label: 'Conversational' },
      { value: 4, label: 'Basic' },
    ];
  }

  handleMasterToggle(enabled: boolean) {
    this.formData.smart_match_enabled = enabled;
    if (!enabled) {
      // Disable all sub-toggles when master is disabled
      this.formData.availability_enabled = false;
      this.formData.exclude_do_not_use_enabled = false;
      this.formData.required_skills_enabled = false;
      this.formData.location_radius_enabled = false;
      this.formData.preferred_language_enabled = false;

      // Disable all nice-to-have criteria
      this.formData.soft_skills_enabled = false;
      this.formData.title_type_match_enabled = false;
      this.formData.previous_engagements_enabled = false;
      this.formData.transportation_enabled = false;
      this.formData.rating_enabled = false;
      this.formData.favourited_resource_enabled = false;
      this.formData.hours_available_enabled = false;

      this.niceToHaveCriteria.forEach(criterion => {
        criterion.enabled = false;
      });
    } else {
      // When enabling, restore default values
      this.formData.availability_enabled = true;
      this.formData.exclude_do_not_use_enabled = true;
      this.formData.required_skills_enabled = true;
      this.formData.location_radius_enabled = true;
      this.formData.preferred_language_enabled = true;

      this.formData.soft_skills_enabled = true;
      this.formData.title_type_match_enabled = true;
      this.formData.previous_engagements_enabled = true;
      this.formData.transportation_enabled = true;
      this.formData.rating_enabled = true;
      this.formData.favourited_resource_enabled = true;
      this.formData.hours_available_enabled = true;

      this.niceToHaveCriteria.forEach(criterion => {
        criterion.enabled = true;
      });
    }
  }

  async mounted() {
    // Initialize slider value based on default form data
    this.previousEngagementsSliderValue = this.engagementsToSliderValue(this.formData.previous_engagements_min);
    await this.loadExistingConfig();
  }

  async loadExistingConfig() {
    try {
      // First try to get manager ID from the user ID
      let managerResult;
      try {
        managerResult = await this.$apollo.query({
          query: GET_MANAGER_BY_USER_ID,
          variables: {
            userId: this.currentUserId,
          },
          fetchPolicy: 'network-only',
        });
      } catch (error) {
        console.log('No manager record found, checking for admin');
      }

      // If no manager found, try to get admin ID
      if (!managerResult?.data.manager.length) {
        try {
          const adminResult = await this.$apollo.query({
            query: GET_ADMIN_BY_USER_ID,
            variables: {
              userId: this.currentUserId,
            },
            fetchPolicy: 'network-only',
          });

          if (!adminResult.data.admin.length) {
            console.error('No manager or admin record found for user');
            return;
          }

          this.adminId = adminResult.data.admin[0].id;
          this.isAdmin = true;
        } catch (error) {
          console.error('Error fetching admin record:', error);
          return;
        }
      } else {
        this.managerId = managerResult.data.manager[0].id;
        this.isAdmin = false;
      }

      let result;
      if (this.isAdmin && this.adminId) {
        result = await this.$apollo.query({
          query: GET_SMART_MATCH_CONFIG_BY_ADMIN,
          variables: { adminId: this.adminId },
          fetchPolicy: 'network-only',
        });
      } else if (this.managerId) {
        result = await this.$apollo.query({
          query: GET_SMART_MATCH_CONFIG_BY_MANAGER,
          variables: { managerId: this.managerId },
          fetchPolicy: 'network-only',
        });
      } else {
        console.error('No manager or admin ID available');
        return;
      }

      if (result.data.smart_match_configs.length > 0) {
        const config = result.data.smart_match_configs[0];

        // Update form data with existing configuration
        this.formData = {
          smart_match_enabled: config.smart_match_enabled,
          availability_enabled: config.availability_enabled,
          exclude_do_not_use_enabled: config.exclude_do_not_use_enabled,
          required_skills_enabled: config.required_skills_enabled,
          location_radius_enabled: config.location_radius_enabled,
          location_radius_value: config.location_radius_value,
          location_radius_unit: config.location_radius_unit,
          preferred_language_enabled: config.preferred_language_enabled,
          preferred_language_proficiency: config.preferred_language_proficiency,
          soft_skills_enabled: config.soft_skills_enabled,
          title_type_match_enabled: config.title_type_match_enabled,
          previous_engagements_enabled: config.previous_engagements_enabled,
          previous_engagements_min: config.previous_engagements_min,
          transportation_enabled: config.transportation_enabled,
          rating_enabled: config.rating_enabled,
          rating_min_stars: config.rating_min_stars,
          favourited_resource_enabled: config.favourited_resource_enabled,
          hours_available_enabled: config.hours_available_enabled,
          hours_available_min: config.hours_available_min,
        };

        // Update transportation selections - match database values to dropdown options
        this.selectedTransportation = config.smart_match_config_transportations.map((t: any) => {
          // Map database values to display names
          const transportationMap = {
            'driving': 'Driving',
            'public_transit': 'Public Transit',
            'cycling': 'Cycling',
            'walking': 'Walking'
          };
          return {
            id: t.transportation_type,
            name: transportationMap[t.transportation_type] || t.transportation_type,
          };
        });

        // Update criteria order and enabled states
        if (config.smart_match_criteria_orders.length > 0) {
          this.niceToHaveCriteria = config.smart_match_criteria_orders.map((order: any) => {
            const defaultCriterion = this.niceToHaveCriteria.find(c => c.type === order.criterion_type);
            return {
              ...defaultCriterion,
              enabled: this.getCriterionEnabledState(order.criterion_type),
              sort_order: order.sort_order,
            };
          });
        }

        // Update slider value after loading config
        this.previousEngagementsSliderValue = this.engagementsToSliderValue(this.formData.previous_engagements_min);
      }
    } catch (error) {
      console.error('Error loading Smart Match configuration:', error);
    }
  }

  getCriterionEnabledState(criterionType: string): boolean {
    const mappings: Record<string, keyof SmartMatchConfig> = {
      'soft_skills': 'soft_skills_enabled',
      'title_type_match': 'title_type_match_enabled',
      'previous_engagements': 'previous_engagements_enabled',
      'transportation': 'transportation_enabled',
      'rating': 'rating_enabled',
      'favourited_resource': 'favourited_resource_enabled',
      'hours_available': 'hours_available_enabled',
    };

    const key = mappings[criterionType];
    return key ? this.formData[key] as boolean : true;
  }

  capitalizeWords(str: string): string {
    return str.replace(/\w\S*/g, (txt) =>
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  // Previous engagements slider mapping methods
  sliderValueToEngagements(sliderValue: number): number {
    const mappings = [0, 1, 4, 7, 10]; // 0, 1-3, 4-6, 7-9, 10+
    return mappings[sliderValue] || 0;
  }

  engagementsToSliderValue(engagements: number): number {
    if (engagements === 0) return 0;
    if (engagements >= 1 && engagements <= 3) return 1;
    if (engagements >= 4 && engagements <= 6) return 2;
    if (engagements >= 7 && engagements <= 9) return 3;
    if (engagements >= 10) return 4;
    return 0;
  }

  getPreviousEngagementsLabel(sliderValue: number): string {
    const labels = ['0', '1-3', '4-6', '7-9', '10+'];
    return labels[sliderValue] || '0';
  }

  // Slider event handlers
  handleSliderMouseDown(event: MouseEvent) {
    event.stopPropagation();
    this.isDraggingSlider = true;
  }

  handleSliderMouseUp(event: MouseEvent) {
    event.stopPropagation();
    this.isDraggingSlider = false;
  }

  handleSliderTouchStart(event: TouchEvent) {
    event.stopPropagation();
    this.isDraggingSlider = true;
  }

  handleSliderTouchEnd(event: TouchEvent) {
    event.stopPropagation();
    this.isDraggingSlider = false;
  }

  async onSubmit() {
    if (!this.managerId && !this.adminId) {
      this.$eventBus.emit(NOTIFICATION, {
        type: "error",
        title: "Error",
        message: "Manager or Admin ID not found. Please refresh and try again.",
      });
      return;
    }

    this.isLoading = true;
    try {
      // Prepare configuration object  
      const config = {
        manager_id: this.isAdmin ? null : this.managerId,
        admin_id: this.isAdmin ? this.adminId : null,
        smart_match_enabled: this.formData.smart_match_enabled,
        availability_enabled: this.formData.availability_enabled,
        exclude_do_not_use_enabled: this.formData.exclude_do_not_use_enabled,
        required_skills_enabled: this.formData.required_skills_enabled,
        location_radius_enabled: this.formData.location_radius_enabled,
        location_radius_value: this.formData.location_radius_value,
        location_radius_unit: this.formData.location_radius_unit,
        preferred_language_enabled: this.formData.preferred_language_enabled,
        preferred_language_proficiency: this.formData.preferred_language_proficiency,
        soft_skills_enabled: this.formData.soft_skills_enabled,
        title_type_match_enabled: this.formData.title_type_match_enabled,
        previous_engagements_enabled: this.formData.previous_engagements_enabled,
        previous_engagements_min: this.formData.previous_engagements_min,
        transportation_enabled: this.formData.transportation_enabled,
        rating_enabled: this.formData.rating_enabled,
        rating_min_stars: this.formData.rating_min_stars,
        favourited_resource_enabled: this.formData.favourited_resource_enabled,
        hours_available_enabled: this.formData.hours_available_enabled,
        hours_available_min: this.formData.hours_available_min,
        updated_at: new Date().toISOString(),
      };

      // Step 1: Save the main configuration
      const mutation = this.isAdmin ? SAVE_SMART_MATCH_CONFIG_ADMIN : SAVE_SMART_MATCH_CONFIG;
      const result = await this.$apollo.mutate({
        mutation,
        variables: {
          config,
        },
      });

      if (result.data && result.data.insert_smart_match_configs_one) {
        const configId = result.data.insert_smart_match_configs_one.id;

        // Step 2: Save transportation preferences using the config ID
        const transportation = this.selectedTransportation.map(t => ({
          config_id: configId,
          transportation_type: t.id,
          created_at: new Date().toISOString(),
        }));

        if (transportation.length > 0) {
          await this.$apollo.mutate({
            mutation: SAVE_TRANSPORTATION_PREFERENCES,
            variables: {
              configId,
              transportation,
            },
          });
        }

        // Step 3: Save criteria order using the config ID
        const criteriaOrder = this.niceToHaveCriteria.map(criterion => ({
          config_id: configId,
          criterion_type: criterion.type,
          sort_order: criterion.sort_order,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }));

        if (criteriaOrder.length > 0) {
          await this.$apollo.mutate({
            mutation: SAVE_CRITERIA_ORDER,
            variables: {
              configId,
              criteriaOrder,
            },
          });
        }
        this.$eventBus.emit(NOTIFICATION, {
          type: "success",
          title: "Success",
          message: "Smart Match configuration saved successfully",
        });
        this.$emit('close');
      }
    } catch (error) {
      console.log('Error saving Smart Match configuration:', error);
      this.$eventBus.emit(NOTIFICATION, {
        type: "error",
        title: "Error",
        message: "Failed to save Smart Match configuration. Please try again.",
      });
    } finally {
      this.isLoading = false;
    }
  }

  closeModal() {
    this.$emit('close');
  }

  // Drag and drop methods
  handleDragStart(index: number, event: DragEvent) {
    if (!this.formData.smart_match_enabled) return;

    // Prevent dragging if the drag originated from a slider or its container
    const target = event.target as HTMLElement;
    const inputTarget = target as HTMLInputElement;
    if ((inputTarget.type && inputTarget.type === 'range') || target.classList.contains('slider') || target.closest('input[type="range"]')) {
      event.preventDefault();
      return;
    }

    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());
    }
  }

  handleDragOver(index: number, event: DragEvent) {
    if (!this.formData.smart_match_enabled) return;

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dragOverIndex = index;
  }

  handleDragLeave() {
    this.dragOverIndex = null;
  }

  handleDrop(dropIndex: number, event: DragEvent) {
    if (!this.formData.smart_match_enabled || this.draggedIndex === null) return;

    event.preventDefault();

    const dragIndex = this.draggedIndex;
    if (dragIndex !== dropIndex) {
      // Reorder the array
      const draggedItem = this.niceToHaveCriteria[dragIndex];
      this.niceToHaveCriteria.splice(dragIndex, 1);
      this.niceToHaveCriteria.splice(dropIndex, 0, draggedItem);

      // Update sort_order values
      this.niceToHaveCriteria.forEach((criterion, index) => {
        criterion.sort_order = index + 1;
      });
    }

    this.draggedIndex = null;
    this.dragOverIndex = null;
  }

  handleDragEnd() {
    this.draggedIndex = null;
    this.dragOverIndex = null;
  }
}
